/* cf-clamp-qty-input.js — let the clamp quantity box be cleared and retyped.
 *
 * Pete, 2026-09-03, reported by a customer: "when you select the number of clamps
 * you cant select it and reduce it unless you use the up and down arrows."
 *
 * THE CAUSE. setupClampFieldListeners binds the SAME handler to both 'input' and
 * 'change' on #section-N-clamp-qty:
 *
 *     let q = parseInt(qtyInput.value) || 1;
 *     if (q < 1) q = 1;
 *     qtyInput.value = q;            // <- on every keystroke
 *
 * Clearing the box fires 'input' with value "", parseInt("") is NaN, `|| 1` turns
 * that into 1, and 1 is written straight back. The field can therefore never be
 * empty, so a customer cannot select-all-and-retype; only the spinner arrows work,
 * because they only ever produce a valid number.
 *
 * THE FIX. Normalising a field while someone is still typing in it is the bug.
 * A half-typed value is not an invalid value, it is an unfinished one. So:
 *   - 'input'  accepts an empty box and never writes to .value; it pushes the
 *              number through to the calculator only once one is actually there.
 *   - 'blur'/'change'/Enter settle the field: empty or nonsense becomes 1, and
 *              anything above max is pulled back to max.
 *
 * The original listeners are anonymous, so they cannot be removed by reference.
 * The input is replaced with a clone instead, which drops every listener on it.
 * That is safe here because those two are the only listeners the calculator binds
 * to this node, and it always re-finds the node by id rather than holding it.
 *
 * Idempotent, and re-applies itself when the calculator re-renders the section.
 */
(function () {
  'use strict';

  var TAG = '[cf-clamp-qty]';
  var QTY_ID_RE = /^section-(\d+)-clamp-qty$/;
  var MAX_TRIES = 200;
  var tries = 0;

  function calc() {
    return window.glassCalculator || window.glassFencingCalculator || null;
  }

  function commit(sectionNum, q) {
    try {
      var c = calc();
      if (!c || !c.state || !c.state.sections) return;
      var s = c.state.sections[sectionNum];
      if (!s) return;
      s.clampQuantity = q;
      if (typeof c.updateQuoteDisplay === 'function') c.updateQuoteDisplay();
      if (typeof c.updateComponentsQuote === 'function') c.updateComponentsQuote();
    } catch (e) {
      console.warn(TAG, 'commit', e);
    }
  }

  function harden(el) {
    if (!el || el.getAttribute('data-cf-clamp-qty')) return false;
    var m = QTY_ID_RE.exec(el.id || '');
    if (!m) return false;
    var sectionNum = m[1];

    var fresh = el.cloneNode(true);
    fresh.setAttribute('data-cf-clamp-qty', '1');
    el.parentNode.replaceChild(fresh, el);

    fresh.addEventListener('input', function () {
      var raw = String(fresh.value).trim();
      if (raw === '') return;                 // the whole point: allow an empty box
      var q = parseInt(raw, 10);
      if (isFinite(q) && q >= 1) commit(sectionNum, q);
    });

    function settle() {
      var q = parseInt(fresh.value, 10);
      if (!isFinite(q) || q < 1) q = 1;
      var max = parseInt(fresh.getAttribute('max'), 10);
      if (isFinite(max) && q > max) q = max;
      if (String(fresh.value) !== String(q)) fresh.value = String(q);
      commit(sectionNum, q);
    }

    fresh.addEventListener('blur', settle);
    fresh.addEventListener('change', settle);
    fresh.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) settle();
    });

    return true;
  }

  function sweep() {
    var n = 0;
    try {
      var nodes = document.querySelectorAll('input[id$="-clamp-qty"]:not([data-cf-clamp-qty])');
      for (var i = 0; i < nodes.length; i++) if (harden(nodes[i])) n++;
    } catch (e) {
      console.warn(TAG, 'sweep', e);
    }
    return n;
  }

  function install() {
    var c = calc();
    if (!c) return false;
    var proto = Object.getPrototypeOf(c);
    if (!proto) return false;
    if (proto.__cfClampQtyPatched) return true;
    if (typeof proto.setupClampFieldListeners !== 'function') return false;

    var orig = proto.setupClampFieldListeners;
    proto.setupClampFieldListeners = function () {
      var out;
      try { out = orig.apply(this, arguments); }
      finally { try { sweep(); } catch (e) { console.warn(TAG, 'post-setup', e); } }
      return out;
    };
    proto.__cfClampQtyPatched = true;

    // The section form can be rebuilt without that method running again, so keep a
    // cheap net under it. Hardened inputs carry a marker and are skipped.
    setInterval(sweep, 1000);
    sweep();

    console.log(TAG, 'installed');
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) { console.warn(TAG, 'install', e); }
    if (!done && ++tries < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInstall);
  } else {
    tryInstall();
  }
})();
